"use client";

/**
 * useCryptoPriceWs - Real-time crypto prices via backend Socket.IO
 *
 * Listens to `crypto:price` events from the shared price socket.
 * Same data source as CryptoPriceChart (Chainlink Data Streams via Polymarket WS).
 *
 * Flow: wss://ws-live-data.polymarket.com → backend → crypto:price → this hook
 */

import { useEffect, useRef, useState } from "react";
import { getOrCreateSharedSocket, getSharedPriceSocket, joinPriceRoom, leavePriceRoom } from "./usePriceStream";
import type { Socket } from "socket.io-client";

export interface CryptoPriceWs {
  price: number;
  timestamp: number;
}

// Batch all price ticks within this window into a single React commit.
// 250 ms = 4 fps perceived update rate, imperceptible to humans but cuts
// renders by ~10× when many symbols stream concurrently. Pure UI display
// hook — no consumer needs sub-frame price granularity.
const FLUSH_INTERVAL_MS = 250;

export function useCryptoPriceWs(enabled: boolean): Record<string, CryptoPriceWs> {
  const [prices, setPrices] = useState<Record<string, CryptoPriceWs>>({});
  const pricesRef = useRef(prices);
  const socketRef = useRef<Socket | null>(null);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Última vez (ms) que vimos un tick de Chainlink por símbolo, para arbitrar.
  const lastChainlinkAtRef = useRef<Record<string, number>>({});

  useEffect(() => {
    if (!enabled) return;

    const handler = (data: { symbol: string; price: number; timestamp: number; source?: string }) => {
      if (!data.symbol || !data.price || data.price <= 0) return;
      const symbol = data.symbol.replace("usdt", "").replace("USDT", "").toUpperCase();
      // Arbitrar fuentes: el backend emite crypto:price desde Chainlink (el
      // oráculo de resolución de PM) Y desde Binance. Sin arbitrar, alternar
      // ambas hacía que el precio —y cualquier chart que lo plotea— zigzagueara
      // entre los dos feeds (valores ligeramente distintos). Preferimos
      // Chainlink; aceptamos Binance solo si Chainlink lleva >4s en silencio.
      // Ticks sin `source` (legacy) se aceptan tal cual. Mismo criterio que
      // CryptoPriceLevelChart.
      const now = Date.now();
      if (data.source === "chainlink") {
        lastChainlinkAtRef.current[symbol] = now;
      } else if (data.source === "binance" && now - (lastChainlinkAtRef.current[symbol] || 0) <= 4000) {
        return; // Chainlink fresco → ignorar el tick de Binance
      }
      pricesRef.current = {
        ...pricesRef.current,
        [symbol]: { price: data.price, timestamp: data.timestamp },
      };
      // Schedule a single flush per window — coalesce N ticks into 1 setState.
      if (flushTimerRef.current) return;
      flushTimerRef.current = setTimeout(() => {
        flushTimerRef.current = null;
        setPrices(pricesRef.current);
      }, FLUSH_INTERVAL_MS);
    };

    // Join the `crypto` measurement room so the backend can count how many
    // sockets actually view crypto (Deploy 1 room-scoping instrument). No-op
    // for data — crypto:price is still broadcast globally.
    let cancelled = false;
    let joined = false;
    const doJoin = () => {
      if (cancelled || joined) return;
      joined = true;
      joinPriceRoom("crypto");
    };

    // Try existing socket first, otherwise create one
    const existing = getSharedPriceSocket();
    if (existing?.connected) {
      socketRef.current = existing;
      existing.on("crypto:price", handler);
      doJoin();
    } else {
      getOrCreateSharedSocket().then(socket => {
        if (cancelled || !socket) return;
        socketRef.current = socket;
        socket.on("crypto:price", handler);
        doJoin();
      });
    }

    return () => {
      cancelled = true;
      socketRef.current?.off("crypto:price", handler);
      if (joined) leavePriceRoom("crypto");
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
    };
  }, [enabled]);

  return prices;
}
